import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { pobierzTeczke, zapiszCzesci, zglosPobranieRysunku } from '../api/teczka'
import { useMozna } from './usePlan'
import { useOknoPremium } from '../components/plany/OknoPremiumContext'
import {
  CZESCI, czesciZPrzegladarki, kontoZTeczka, oznaczProjektNiezapisany, projektZbudowany, przejmijPrzegladarke,
  tenSamBudynek, zapiszCzesciWPrzegladarce, znakKonta,
} from '../lib/teczka/konto'

/**
 * Teczka instalacji konta prywatnego (2026-10-23, umowa o danych w repo VV `zadania/2026-10_teczka-instalacji.md`).
 *
 * `const { teczka, ustalanie, dostepna, darmowyProjekt, zapis, zapiszCzesc, zglosPobranie } = useTeczka()`
 * - `teczka`: części z konta (`projekt`, `sprzet`, `pieniadze`, `miejsca`, `nazwa`, daty) albo null, gdy jeszcze nic
 *   nie zapisano; bez konta prywatnego części z zapasu w przeglądarce.
 * - `dostepna`: zalogowane konto prywatne, a backend zna teczkę. Konto firmowe nie ma teczki (projekt w karcie klienta).
 * - `zapiszCzesc('miejsca', dane)`: zapis sekundę po ostatniej zmianie; tylko ta część, pozostałe zostają.
 *   Zapas w przeglądarce od razu (`vv:teczka`, bez projektu). Niezmieniona część niczego nie wysyła.
 * - `zapis.stan`: null, `zapisuje`, `zapisano`, `blad` (zostaje w przeglądarce, ponowimy przy następnej zmianie)
 *   albo `premium` (inny budynek po zużyciu „raz” bez Premium, okno Premium otworzył api/apiFetch).
 * - `zglosPobranie(projekt)`: pobranie pliku rysunku (zużywa „raz” przy pierwszym razie); `{ mozna }`.
 */
export const useTeczka = () => {
  const { isAuthenticated, loading, profilUstalany, profil, odswiezProfil } = useAuth()
  const dostepna = Boolean(isAuthenticated && kontoZTeczka(profil))
  const ustalanieKonta = Boolean(loading || profilUstalany)
  const zMe = profil?.teczka?.darmowy_projekt || null
  const znak = dostepna && profil?.email ? znakKonta(profil.email) : null

  const [stan, setStan] = useState({ ustalanie: true, teczka: null, darmowyProjekt: null, bladWczytania: null })
  const [zapis, setZapis] = useState({ stan: null, blad: null })
  const dostepnaRef = useRef(dostepna)
  dostepnaRef.current = dostepna
  const zMeRef = useRef(zMe)
  zMeRef.current = zMe
  const zapisane = useRef({})
  const oczekujace = useRef({})
  const czas = useRef(null)
  const trwa = useRef(false)
  const ponow = useRef(false)

  useEffect(() => {
    if (ustalanieKonta) return undefined
    if (!dostepna) {
      setStan({ ustalanie: false, teczka: czesciZPrzegladarki(), darmowyProjekt: null, bladWczytania: null })
      return undefined
    }
    // Cudza praca z przeglądarki (wspólny komputer) znika, zanim trafiłaby do tej teczki.
    przejmijPrzegladarke(znak)
    let anulowane = false
    const wczytaj = async () => {
      const w = await pobierzTeczke()
      if (anulowane) return
      if (!w.ok) {
        setStan({ ustalanie: false, teczka: null, darmowyProjekt: zMeRef.current, bladWczytania: w.error })
        return
      }
      let { teczka } = w.data
      let darmowy = w.data.darmowy_projekt
      // Części zapisane wcześniej tylko w przeglądarce (np. przed tym zapisem na koncie) idą na konto raz.
      const lokalne = czesciZPrzegladarki()
      const brakujace = Object.fromEntries(CZESCI
        .filter((k) => k !== 'projekt' && teczka?.[k] == null && lokalne[k] != null)
        .map((k) => [k, lokalne[k]]))
      if (Object.keys(brakujace).length) {
        const z = await zapiszCzesci(brakujace)
        if (anulowane) return
        if (z.ok) ({ teczka, darmowy_projekt: darmowy } = z.data)
      }
      CZESCI.forEach((k) => { zapisane.current[k] = JSON.stringify(teczka?.[k] ?? null) })
      if (teczka) zapiszCzesciWPrzegladarce(teczka)
      setStan({ ustalanie: false, teczka, darmowyProjekt: darmowy, bladWczytania: null })
    }
    wczytaj()
    return () => { anulowane = true }
  }, [ustalanieKonta, dostepna, znak])

  const wyslij = useCallback(async () => {
    if (trwa.current) { ponow.current = true; return }
    const czesci = oczekujace.current
    if (!Object.keys(czesci).length) return
    oczekujace.current = {}
    trwa.current = true
    setZapis({ stan: 'zapisuje', blad: null })
    const w = await zapiszCzesci(czesci)
    trwa.current = false
    if (w.ok) {
      Object.keys(czesci).forEach((k) => { zapisane.current[k] = JSON.stringify(czesci[k] ?? null) })
      if ('projekt' in czesci) oznaczProjektNiezapisany(false)
      setStan((s) => ({ ...s, teczka: w.data.teczka, darmowyProjekt: w.data.darmowy_projekt }))
      setZapis({ stan: 'zapisano', blad: null })
    } else {
      // Do ponowienia przy następnej zmianie; nowsza wersja części wygrywa. Projektu odrzuconego jako inny budynek
      // nie ponawiamy, bo każda próba otwierałaby okno Premium.
      const doPonowienia = { ...czesci }
      if (w.httpStatus === 402) delete doPonowienia.projekt
      oczekujace.current = { ...doPonowienia, ...oczekujace.current }
      setZapis({ stan: w.httpStatus === 402 ? 'premium' : 'blad', blad: w.error })
    }
    if (ponow.current) { ponow.current = false; wyslij() }
  }, [])

  const zapiszCzesc = useCallback((nazwa, dane, { odroczenie = 1000 } = {}) => {
    if (!CZESCI.includes(nazwa) && nazwa !== 'nazwa') return
    const json = JSON.stringify(dane ?? null)
    if (nazwa !== 'projekt') {
      zapiszCzesciWPrzegladarce({ [nazwa]: dane })
      setStan((s) => ({ ...s, teczka: { ...(s.teczka || {}), [nazwa]: dane } }))
    }
    if (!dostepnaRef.current) return
    if (json === zapisane.current[nazwa]) { delete oczekujace.current[nazwa]; return }
    if (nazwa === 'projekt') oznaczProjektNiezapisany(true)
    oczekujace.current[nazwa] = dane
    clearTimeout(czas.current)
    czas.current = setTimeout(wyslij, odroczenie)
  }, [wyslij])

  // Wyjście ze strony przed upływem odroczenia: wysyłamy od razu (projekt i tak jest w przeglądarce).
  useEffect(() => () => {
    clearTimeout(czas.current)
    if (Object.keys(oczekujace.current).length) wyslij()
  }, [wyslij])

  const zglosPobranie = useCallback(async (projekt) => {
    if (!dostepnaRef.current) return { ok: true, mozna: true }
    delete oczekujace.current.projekt
    const w = await zglosPobranieRysunku({ projekt })
    if (w.ok) {
      zapisane.current.projekt = JSON.stringify(projekt ?? null)
      oznaczProjektNiezapisany(false)
      setStan((s) => ({ ...s, teczka: w.data.teczka, darmowyProjekt: w.data.darmowy_projekt }))
      if (odswiezProfil) odswiezProfil()
    }
    if (Object.keys(oczekujace.current).length) wyslij()
    return w
  }, [wyslij, odswiezProfil])

  // „Zacznij nowy projekt” w Projekcie 3D (prośba właściciela 2026-10-26): projekt i sprzęt znikają z teczki od razu,
  // w jednym zapisie. Prosty audyt (`miejsca`) i pieniądze zostają; migawki dołączone do zleceń backend trzyma osobno.
  const wyczyscProjekt = useCallback(() => {
    setStan((s) => (s.teczka ? { ...s, teczka: { ...s.teczka, projekt: null, sprzet: null } } : s))
    zapiszCzesc('projekt', null, { odroczenie: 0 })
    zapiszCzesc('sprzet', null, { odroczenie: 0 })
  }, [zapiszCzesc])

  return { ...stan, dostepna, zapis, zapiszCzesc, zglosPobranie, wyczyscProjekt }
}

/**
 * „Raz za darmo” w Projekcie 3D (decyzje właściciela 2026-10-23): po pobraniu rysunku konto prywatne bez Premium
 * nie zmieni budynku (inny adres na mapie, „Nowy projekt”, przejście na wymiary bez mapy); zmiany w tym budynku bez
 * limitu. Zablokowana zmiana otwiera okno Premium. Backend pilnuje tego samego (402), to tylko wygoda interfejsu.
 * `teczka` z `useTeczka()` albo null (konto firmowe: nic się nie zmienia).
 */
export const useBudynekTeczki = ({ teczka, projekt }) => {
  const { mozna: premium } = useMozna('projekt3d')
  const { otworz } = useOknoPremium()
  const wTeczce = Boolean(teczka?.dostepna)
  const zablokowany = wTeczce && !premium && teczka.darmowyProjekt === 'zuzyty'
  // Budynek, z którym porównuje backend: projekt zapisany na koncie (bez niego obecny).
  const wzor = teczka?.teczka?.projekt || projekt

  const pozwolNaBudynek = useCallback((srodek, obrys = null) => {
    if (!zablokowany || tenSamBudynek(wzor, srodek, obrys)) return true
    otworz('projekt3d')
    return false
  }, [zablokowany, wzor, otworz])

  const pozwolNaNowy = useCallback(() => {
    if (!zablokowany) return true
    otworz('projekt3d')
    return false
  }, [zablokowany, otworz])

  let ostrzezenieNowego
  if (zablokowany) ostrzezenieNowego = 'Inny budynek jest w planie Premium. Ten projekt zostaje w Twojej teczce.'
  else if (wTeczce && projektZbudowany(wzor)) ostrzezenieNowego = 'Nowy budynek zastąpi projekt w Twojej teczce.'

  return useMemo(() => ({ wTeczce, premium, zablokowany, pozwolNaBudynek, pozwolNaNowy, ostrzezenieNowego }),
    [wTeczce, premium, zablokowany, pozwolNaBudynek, pozwolNaNowy, ostrzezenieNowego])
}

export default useTeczka
